/* eslint-disable react-refresh/only-export-components */
import { BroadviewClientError } from "@broadview/api-client";
import { AlertTriangle, LockKeyhole, RefreshCw } from "lucide-react";

export function portalDataErrorCopy(error: unknown) {
  const typed = error instanceof BroadviewClientError ? error : null;
  const forbidden = typed?.code === "FORBIDDEN";
  const mismatch = typed?.code === "CONTRACT_MISMATCH";
  return {
    typed,
    forbidden,
    mismatch,
    detail: forbidden
      ? "Your verified account does not have permission for this Division view."
      : mismatch
        ? "The service response did not match the approved portal contract. No fixture data was substituted."
        : (typed?.message ??
          "The Broadview service is temporarily unavailable. No fixture data was substituted."),
  };
}

export function PortalDataError({
  error,
  onRetry,
  title = "This live view could not be loaded",
}: Readonly<{ error: unknown; onRetry: () => void; title?: string }>) {
  const { typed, forbidden, detail } = portalDataErrorCopy(error);
  const Icon = forbidden ? LockKeyhole : AlertTriangle;

  return (
    <section role="alert" className="rounded-xl border border-warning/40 bg-warning/10 p-6">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{detail}</p>
          {typed?.correlationId && (
            <p className="mt-2 text-xs text-muted-foreground">Reference: {typed.correlationId}</p>
          )}
          {!forbidden && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold hover:bg-muted"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry live read
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export function PortalLoadingState({ label }: { label: string }) {
  return (
    <section aria-live="polite" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <span className="sr-only">{label}</span>
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="h-32 animate-pulse rounded-xl border border-border bg-card" />
      ))}
    </section>
  );
}
